import { exhaustiveGuard } from "@breakout/shared/utils"
import { useCountdown } from "../../hooks/common/useCountdown"

/** Discriminated union for market description props */
type MarketDescriptionProps = {
	/** Market close time in milliseconds */
	closeTimeMs: number | null
	/** Optional override description for custom markets */
	customDescription?: string | null
} & ({ marketType: "binary"; creatorName: string } | { marketType: "fall_from_grace" })

/**
 * Displays a brief description of the market mechanics and resolution.
 * Shows different copy for binary vs Fall from Grace markets.
 * When customDescription is provided, renders it directly instead of generated text.
 */
export function MarketDescription(props: MarketDescriptionProps) {
	const { timeRemaining, isEnded } = useCountdown(props.closeTimeMs)

	// Custom description override for custom markets
	if (props.customDescription) {
		return (
			<div className="mt-3 space-y-2 text-pretty text-sm text-text-secondary sm:text-base">
				{props.customDescription.split("\n\n").map(paragraph => (
					<p key={paragraph}>{paragraph}</p>
				))}
			</div>
		)
	}

	const countdownText = isEnded ? "when market closes" : `in ${timeRemaining}`

	switch (props.marketType) {
		case "fall_from_grace":
			return (
				<p className="mt-3 text-pretty text-sm text-text-secondary sm:text-base">
					Bet on who will have the biggest fall in CT mindshare next week. 5 contenders, 1 loser.
					Resolution is based on{" "}
					<a
						className="font-medium text-text-primary underline decoration-text-muted underline-offset-2 transition-colors hover:decoration-text-primary"
						href="https://kaito.ai/mindshare-arena/ct-leaderboard"
						rel="noopener noreferrer"
						target="_blank"
					>
						Kaito Mindshare CT Leaderboard 7D
					</a>{" "}
					data at market close {countdownText}.
				</p>
			)
		case "binary":
			return (
				<p className="mt-3 text-pretty text-sm text-text-secondary sm:text-base">
					Bet on whether {props.creatorName}'s CT mindshare will rise or fall next week. Resolution
					is based on{" "}
					<a
						className="font-medium text-text-primary underline decoration-text-muted underline-offset-2 transition-colors hover:decoration-text-primary"
						href="https://kaito.ai/mindshare-arena/ct-leaderboard"
						rel="noopener noreferrer"
						target="_blank"
					>
						Kaito Mindshare CT Leaderboard 7D
					</a>{" "}
					data at market close {countdownText}. If the mindshare rank is exactly the same, this
					market resolves to Long {props.creatorName}.
				</p>
			)
		default:
			return exhaustiveGuard(props)
	}
}
